import { useState } from 'react';
import Dice from './Dice';
import SmallDice from './SmallDice';
import '../styles/GameBoard.css';

const GAME_CONFIG = {
  GST1: {
    colors: ['#FFD700', '#FF8C00', '#FFFFFF', '#00AA00', '#0000FF', '#800080'],
    colorNames: ['yellow', 'orange', 'white', 'green', 'blue', 'purple']
  },
  GST2: {
    colors: ['#00AA00', '#0000FF', '#FFD700', '#FFFFFF', '#FF1493', '#C0C0C0'],
    colorNames: ['green', 'blue', 'yellow', 'white', 'pink', 'silver']
  }
};

function GameBoard({ gameType, onBackToMenu }) {
  const config = GAME_CONFIG[gameType];
  const numDice = 6;

  // Game state
  const [rolled, setRolled] = useState(false);
  const [isRolling, setIsRolling] = useState(false);
  const [dice, setDice] = useState(() => initializeDice());
  const [slots, setSlots] = useState([null, null, null]);
  const [platter, setPlatter] = useState([]);
  const [selectedDieIndex, setSelectedDieIndex] = useState(null);
  const [selectedPlatterIndex, setSelectedPlatterIndex] = useState(null);
  const [message, setMessage] = useState('Roll the dice to start!');
  const [gameHistory, setGameHistory] = useState([]);

  function initializeDice() {
    return Array(numDice).fill(null).map((_, i) => ({
      id: i,
      value: 0,
      inPlay: true,
      color: config.colors[i],
      colorName: config.colorNames[i]
    }));
  }

  const rollDice = () => {
    setIsRolling(true);
    setSelectedDieIndex(null);

    // Animation takes about 900ms (15 steps * 60ms)
    setTimeout(() => {
      const newDice = dice.map(die => {
        if (die.inPlay) {
          return { ...die, value: Math.floor(Math.random() * 6) + 1 };
        }
        return die;
      });
      setDice(newDice);
      setRolled(true);
      setIsRolling(false);
      setMessage('Select a die to place in your next slot');
    }, 950);
  };

  const selectDie = (index) => {
    if (dice[index].inPlay && rolled && selectedDieIndex === null) {
      const selectedDie = dice[index];
      const nextSlotIndex = slots.findIndex(s => s === null);

      if (nextSlotIndex === -1) {
        setMessage('All slots are full!');
        return;
      }

      // Place die in next available slot
      const newSlots = [...slots];
      newSlots[nextSlotIndex] = selectedDie;

      // Mark die as no longer in play
      const newDice = dice.map((die, idx) => {
        if (idx === index) {
          return { ...die, inPlay: false };
        }
        return die;
      });

      // Move dice with lower values to platter and update inPlay
      const newPlatter = [...platter];
      const finalDice = newDice.map((die) => {
        if (!die.inPlay) {
          return die; // Already placed
        }
        if (die.value < selectedDie.value) {
          newPlatter.push({
            value: die.value,
            color: die.color,
            colorName: die.colorName
          });
          return { ...die, inPlay: false };
        }
        return die;
      });

      // Save history for undo
      setGameHistory([...gameHistory, {
        dice: dice,
        slots: slots,
        platter: platter,
        selectedDieIndex: index
      }]);

      setDice(finalDice);
      setSlots(newSlots);
      setPlatter(newPlatter);
      setSelectedDieIndex(null);
      setRolled(false);

      const slotsFilledCount = newSlots.filter(s => s !== null).length;
      if (slotsFilledCount === 3) {
        setMessage('All slots filled! Click Reset to play again');
      } else {
        setMessage('Die placed! Roll again to continue');
      }
    }
  };

  const undo = () => {
    if (gameHistory.length === 0) {
      setMessage('Nothing to undo!');
      return;
    }

    const lastState = gameHistory[gameHistory.length - 1];
    setDice(lastState.dice);
    setSlots(lastState.slots);
    setPlatter(lastState.platter);
    setRolled(true);
    setGameHistory(gameHistory.slice(0, -1));
    setMessage('Undo complete! Select another die or roll again');
  };

  const reRoll = () => {
    const hasRemainingDice = dice.some(d => d.inPlay);
    if (!hasRemainingDice) {
      setMessage('No dice left to roll');
      return;
    }
    rollDice();
  };

   const reset = () => {
    setDice(initializeDice());
    setSlots([null, null, null]);
    setPlatter([]);
    setSelectedDieIndex(null);
    setSelectedPlatterIndex(null);
    setRolled(false);
    setGameHistory([]);
    setMessage('Roll the dice to start!');
  };

  const returnPlatterDieToHand = (index) => {
    if (selectedPlatterIndex === index) {
      // Deselect if clicking again
      setSelectedPlatterIndex(null);
      setMessage('Platter die deselected');
      return;
    }

    // Select the die from platter
    setSelectedPlatterIndex(index);
    setMessage('Click "Return to Hand" to bring this die back for rolling');
  };

  const confirmReturnDieToHand = () => {
    if (selectedPlatterIndex === null) {
      setMessage('Select a die from the platter first');
      return;
    }

    const selectedDie = platter[selectedPlatterIndex];

    // Find the original die in the dice array by color and value
    const newDice = dice.map(die => {
      if (die.color === selectedDie.color && die.value === selectedDie.value && !die.inPlay) {
        return { ...die, inPlay: true };
      }
      return die;
    });

    // Remove the die from platter
    const newPlatter = platter.filter((_, idx) => idx !== selectedPlatterIndex);

    setDice(newDice);
    setPlatter(newPlatter);
    setSelectedPlatterIndex(null);
    setMessage('Die returned to hand! You can now roll again.');
  };

  const inPlayDice = dice.filter(d => d.inPlay);
  const getRemainingDiceCount = () => dice.filter(d => d.inPlay && d.value !== 0).length;
  const canSelectDie = rolled && selectedDieIndex === null && slots.some(s => s === null);

  return (
    <div className="game-board">
      <div className="game-header">
        <h1>{gameType}</h1>
        <button className="btn-back" onClick={onBackToMenu}>Back to Menu</button>
      </div>

      <div className="game-container">
        <div className="dice-roll-section">
          <div className="dice-display">
            {inPlayDice.map((die, idx) => (
              <Dice
                key={die.id}
                die={die}
                isSelected={selectedDieIndex === dice.indexOf(die)}
                onSelect={() => selectDie(dice.indexOf(die))}
                isRolling={isRolling}
              />
            ))}
          </div>

          <div className="control-buttons">
            {!rolled ? (
              <button className="btn-primary" onClick={rollDice} disabled={isRolling}>
                {isRolling ? 'Rolling...' : 'Roll Dice'}
              </button>
            ) : (
              <>
                <button className="btn-primary" onClick={rollDice} disabled={isRolling}>
                  {isRolling ? 'Rolling...' : 'Roll Again'}
                </button>
                <button
                  className="btn-secondary"
                  onClick={reRoll}
                  disabled={getRemainingDiceCount() === 0 || isRolling}
                >
                  Re-roll Remaining
                </button>
              </>
            )}
            {selectedPlatterIndex !== null && (
              <button className="btn-return" onClick={confirmReturnDieToHand}>
                ↶ Return to Hand
              </button>
            )}
            {gameHistory.length > 0 && (
              <button className="btn-undo" onClick={undo}>
                ↶ Undo
              </button>
            )}
            <button className="btn-reset" onClick={reset}>
              Reset
            </button>
          </div>

          <div className="message-box">{message}</div>
        </div>

        <div className="game-layout">
          <div className="slots-section">
            <h2>Your Slots</h2>
            <div className="slots">
              {slots.map((slot, idx) => (
                <div
                  key={idx}
                  className={`slot ${slot ? 'filled' : ''}`}
                >
                  {slot ? (
                    <SmallDice die={slot} />
                  ) : (
                    <span className="slot-placeholder">Slot {idx + 1}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="platter-section">
            <h2>Platter</h2>
            <div className="platter">
              {platter.length === 0 ? (
                <span className="platter-empty">Empty</span>
              ) : (
                platter.map((die, idx) => (
                  <div key={idx} className="platter-item">
                    <SmallDice
                      die={die}
                      isClickable={true}
                      isSelected={selectedPlatterIndex === idx}
                      onSelect={() => returnPlatterDieToHand(idx)}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GameBoard;

